import React, { useState, useEffect } from 'react';
import { useRouteData, useSiteData, Head } from 'react-static';
// template components
import ContentHub from '@nmx/template/dist/componentLibrary/modules/ContentHub';
import InPageLeadForm from '@nmx/template/dist/componentLibrary/modules/InPageLeadForm';
import Page from '@nmx/template/dist/componentLibrary/layout/Page';
import Theme from '@nmx/template/dist/componentLibrary/Theme';
import WidowBlock from '@nmx/template/dist/componentLibrary/components/WidowBlock';
// native components
import AdvisorConnectModule from './components/advisorConnectModule';
import CredentialCalloutGrouping from './components/credentialCalloutGrouping';
import Faq from './components/faq';
import HeroSection from './components/heroSection';
import InPageLeadFormLazyLoadContainer from '../../components/inPageLeadFormLazyLoadContainer';
import IntroSection from './components/introSection';
import Metadata from './components/metadata';
import QuizCallout from './components/quizCallout';
import UnderstandingProduct from './components/understandingProduct';
// images for prefetch
import highResDesktopImage from './components/heroSection/images/estate-planning-hero-desktop.webp';
import highResMobileImage from './components/heroSection/images/estate-planning-hero-mobile.webp';
// dislaimer footnotes
import { LoyaltyBasedOnClientData } from '../../../utils/disclaimerFootnotes/disclaimerFootnotes';
// other utils
import { getImmediateMatchFeatureFlag } from '../../../utils/getFeatureFlags/getFeatureFlags';

export default () => {
  const { config, dynamicConfig } = useSiteData();
  const { pageProps, contentHubItems } = useRouteData();
  // We don't want to load recaptcha on page load (for performance reasons) so we will load it once the in page lead for is engaged with
  const [loadRecaptcha, setLoadRecaptcha] = useState(false);
  const [immediateMatchFeatureFlag, setImmediateMatchFeatureFlag] = useState(false);

  useEffect(() => {
    getImmediateMatchFeatureFlag(setImmediateMatchFeatureFlag, config);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Page
      {...pageProps} // defined in route.js
      headComponent={Head}
      dynamicConfig={dynamicConfig}
      disableDisclaimerTopBorder
      config={config}
      useReCaptcha={loadRecaptcha}
      breadcrumbsNavigationConfig={[
        {
          pageName: 'Home',
          linkUrl: '/',
          linkAriaLabel: 'Northwestern Mutual Home',
        },
        {
          pageName: 'Financial Planning',
          linkUrl: '/financial-planning/',
          linkAriaLabel: 'Northwestern Mutual Financial Planning',
        },
        {
          pageName: 'Estate Planning',
          linkUrl: '/financial-planning/estate-planning/',
          linkAriaLabel: 'Northwestern Mutual Estate Planning',
        },
      ]}
      customDisclaimers={[
        <>
          {LoyaltyBasedOnClientData(1)}
        </>,
      ]}>
      <Head>
        {/* prefetch expensive images */}
        <link rel='preload' href={highResMobileImage} as='image' media={`(max-width: ${Theme.breakpoints.values.medium}px)`} />
        <link rel='preload' href={highResDesktopImage} as='image' media={`(min-width: ${Theme.breakpoints.values.medium + 1}px)`} />
      </Head>

      <Metadata
        image={highResDesktopImage}
        pageDescription={pageProps.pageDescription}
        pageTitle={pageProps.pageTitle}
      />

      <HeroSection />

      <IntroSection />

      <UnderstandingProduct />

      <Faq />

      <QuizCallout />

      {/* IN-PAGE LEAD FORM SECTION */}
      <InPageLeadFormLazyLoadContainer>
        <InPageLeadForm
          sectionId='in-page-lead' // note: id in-page-lead-form will be on the Form element itself.
          config={config}
          formLocation='estate-planning'
          immediateMatchFeatureFlagEnabled={immediateMatchFeatureFlag}
          onFormEngaged={() => {
            if (!loadRecaptcha) {
              setLoadRecaptcha(true);
            }
          }}
          variant='embedded'
        />
      </InPageLeadFormLazyLoadContainer>

      <CredentialCalloutGrouping />

      <ContentHub
        sectionId='content-hub'
        backgroundVariant='lightC'
        themeType='lightTheme'
        headingText={<>Boost your estate <WidowBlock>planning knowledge</WidowBlock></>}
        contentHubItems={contentHubItems} // defined in route.js
        config={config}
      />

      <AdvisorConnectModule />

    </Page>
  );
};
